import { FormInstance } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../lib/store/hooks';
import {
  clearDateFilters,
  clearListFilters,
  getParsedSelectedItems,
  LIST_VALUE_PROP_NAME,
} from '../../../lib/utils/arranges-helpers';
import { ArrangeFormItem } from '../../../lib/models/Arrange';
import { TableSortOrder } from '../../../lib/utils/constants';
import { setFilters, setPage, ProductsFiltersPayload } from '../../../lib/store/slices/productsSlice';
import { isArrayNotEmpty, isObjectEmpty } from '../../../lib/utils/helpers';
import { closeModalAfterApply } from '../../../features/table/helpers/closeModalAfterApply';
import { PRODUCTS_COLUMNS } from '../helpers/consts';
import { useMemo } from 'react';

type Props = {
  arrangedId: string;
  form: FormInstance<any>;
  setFilterLabel?: any;
  getFilters?: any;
  getFilterLabel?: any;
  filterFieldName?: string;
  isParsedByValue?: boolean;
  isDate?: boolean;
};

export const useProductsArrangeRequest = ({
  arrangedId,
  setFilterLabel,
  getFilters,
  getFilterLabel,
  form,
  filterFieldName,
  isParsedByValue = false,
  isDate = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const sortingAndFiltersData = useAppSelector(({ products }) => products);

  const clearFormFilters = () => {
    isDate ? clearDateFilters(form) : clearListFilters(form);
  };

  const sortFieldName = useMemo(() => {
    if (arrangedId === PRODUCTS_COLUMNS.CATEGORY) {
      return 'category.name';
    }

    if (arrangedId === PRODUCTS_COLUMNS.DEMAND_AREA) {
      return 'category.demandArea.name';
    }

    return arrangedId;
  }, [arrangedId]);

  const applyArranges = async () => {
    const values = form.getFieldsValue();
    const sort = values[ArrangeFormItem.SORT_ORDER];
    const filters = getFilters?.();

    const parsedItems = getParsedSelectedItems(form);

    const searchItems = parsedItems.map((item: { value: string; label: string }) => {
      if (isParsedByValue) {
        return item.value;
      } else {
        return item.label;
      }
    });

    const filter =
      arrangedId === PRODUCTS_COLUMNS.IS_ACTIVE
        ? {
            activated: searchItems.length > 1 ? null : searchItems[0],
          }
        : searchItems;

    const resultedFilters = {
      arrangeId: arrangedId as PRODUCTS_COLUMNS,
      sorting: {
        sortOrder: sort as TableSortOrder,
        sortFieldName,
        withSorting: true,
      },
      filtering: {
        filterFieldName,
        filter: isDate ? filters : filter,
        filtersWithLabels: isParsedByValue ? parsedItems : [],
      },
    } as ProductsFiltersPayload;

    const isFilterChanged =
      isArrayNotEmpty(resultedFilters.filtering.filter) ||
      !isObjectEmpty(resultedFilters.filtering.filter as any);

    if (isFilterChanged) {
      dispatch(setPage(1));
    }

    dispatch(setFilters(resultedFilters));
    setFilterLabel?.(getFilterLabel());
    closeModalAfterApply(arrangedId);
  };

  const removeFilter = async () => {
    dispatch(
      setFilters({
        arrangeId: arrangedId,
        sorting: { ...sortingAndFiltersData.sorting },
        filtering: { filterFieldName: '', filter: [], filtersWithLabels: [] },
      } as ProductsFiltersPayload),
    );
    setFilterLabel?.(null);
    clearFormFilters();
  };

  const removeFilterValue = async (value: any) => {
    const parsedItems = getParsedSelectedItems(form);
    const filteredItems = parsedItems.filter(
      (item: { [x: string]: any }) => item[LIST_VALUE_PROP_NAME] !== value,
    );

    const updatedFiltersData: ProductsFiltersPayload = {
      ...sortingAndFiltersData,
      arrangedId: sortingAndFiltersData.arrangeId,
      filtering: {
        filterFieldName: sortingAndFiltersData.filtering.filterFieldName,
        filter: filteredItems.map((item: { value: string; label: string }) => {
          if (isParsedByValue) {
            return item.value;
          } else {
            return item.label;
          }
        }),
      },
    } as ProductsFiltersPayload;

    dispatch(setFilters(updatedFiltersData));

    const stringifiedItems = filteredItems.map(JSON.stringify);
    form.setFieldValue(ArrangeFormItem.SELECTED_ITEMS, stringifiedItems);

    const filterLabel = getFilterLabel();
    setFilterLabel?.(filterLabel);

    if (!filterLabel) {
      clearFormFilters();
    }
  };

  return { applyArranges, removeFilter, removeFilterValue };
};
